import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { useTranslation } from 'react-i18next'

import Switch from 'components/Shared/Controls/Switch'
import CoCreatePanelHeader from './CoCreatePanelHeader'
import { groupMediaBySubGroup } from '../../buildMediaListItems'
import { buildMediaSlots } from '../../media'
import ErrorMessage from 'components/Shared/ErrorMessage'
import InfoBox from 'components/Shared/InfoBox'
import classnames from 'classnames'
import CoCreateCredits from './CoCreateCredits'
import {
  DEFAULT_IMAGE_CREDIT_COST,
  estimateGeneratedImageCount,
} from './imageGenerationCredits'
import GenerateButton from './GenerateButton'
import { WalletMinimalIcon, TriangleAlertIcon } from 'images'
import { isNumber } from 'lodash'

const isSubGroupFullyFilled = (items, requiredSlotRatios) => {
  const slots = buildMediaSlots(items, requiredSlotRatios)
  return slots.length > 0 && slots.every((slot) => Boolean(slot.image))
}

export default function CoCreateImagePanel({
  variant,
  setVariant,
  selectedSubGroupId = null,
  setSelectedSubGroupId,
  media = [],
  headlines = [],
  maxItems = 10,
  generatedMediaCount = 0,
  showGenerationError = false,
  onGenerate,
  isCampaignRunning = false,
  onRequestHeadlines,
  requiredSlotRatios,
  onViewVariants,
  creditStatus,
  creditStatusError,
  creditStatusLoading = false,
  onRetryCreditStatus,
}) {
  const filteredHeadlines = useMemo(
    () => headlines.map((headline) => headline?.trim()).filter(Boolean),
    [headlines],
  )
  const hasHeadlines = filteredHeadlines.length > 0
  const countToCreate = filteredHeadlines.length

  const mediaGroups = useMemo(() => groupMediaBySubGroup(media), [media])
  const selectedGroup = useMemo(
    () => mediaGroups.find((group) => String(group.groupKey) === String(selectedSubGroupId)),
    [selectedSubGroupId, mediaGroups],
  )
  const selectedGroupItems = selectedGroup?.items ?? []
  const selectedMediaCount = selectedGroupItems.filter((item) => item.url).length
  const missingAspectRatioCount = buildMediaSlots(selectedGroupItems, requiredSlotRatios)
    .filter((slot) => !slot.image)
    .length
  const mediaGroupsCount = mediaGroups.length
  const remainingSlots = Math.max(0, maxItems - mediaGroupsCount - (generatedMediaCount ?? 0))
  const canUseHeadlines = hasHeadlines && remainingSlots >= countToCreate
  const countToRemove = Math.max(0, countToCreate - remainingSlots)

  const allAspectRatioSlotsFilled = useMemo(() => {
    if (!selectedSubGroupId) return false

    return selectedGroup ? isSubGroupFullyFilled(selectedGroup.items, requiredSlotRatios) : false
  }, [selectedSubGroupId, mediaGroups, requiredSlotRatios])

  const isGenerationLimitReached = useMemo(() => {
    if (remainingSlots !== 0 || !mediaGroups.length) return false

    return mediaGroups.every((group) => isSubGroupFullyFilled(group.items, requiredSlotRatios))
  }, [remainingSlots, mediaGroups, requiredSlotRatios])

  const canUseAspectRatio = !allAspectRatioSlotsFilled
  const isMidGenerationFlow = variant === 'ready' || variant === 'generating'
  const isPanelDisabled = isCampaignRunning || (isGenerationLimitReached && !isMidGenerationFlow)

  const [aspectRatio, setAspectRatio] = useState(canUseAspectRatio)
  const [headlineOverlay, setHeadlineOverlay] = useState(canUseHeadlines)

  const generatedImageCount = estimateGeneratedImageCount({
    currentImageCount: selectedMediaCount,
    headlineCount: headlineOverlay ? filteredHeadlines.length : 0,
    missingAspectRatioCount,
    wantsAspectRatios: aspectRatio,
  })
  const costPerImage = creditStatus?.credits?.cost_per_image ?? DEFAULT_IMAGE_CREDIT_COST
  const totalCreditCost = generatedImageCount * costPerImage
  const remainingCredits = creditStatus?.credits?.remaining
  const isTermsBlocked = creditStatus?.credit_access?.state === 'v4_acceptance_required'
  const hasInsufficientCredits = 
    selectedGroup 
    && generatedImageCount > 0
    && remainingCredits != null
    && totalCreditCost > Number(remainingCredits)
  const isCreditCheckBlocking = creditStatusLoading
    || Boolean(creditStatusError)
    || !creditStatus
    || isTermsBlocked
    || hasInsufficientCredits

  const { t } = useTranslation()

  const prevVariantRef = useRef(variant)
  const manuallyDisabledAspectRatioRef = useRef(false)

  useEffect(() => {
    if (prevVariantRef.current === 'closed' && variant === 'open') {
      setHeadlineOverlay(canUseHeadlines)
      setAspectRatio(canUseAspectRatio)
      manuallyDisabledAspectRatioRef.current = false
    }
    prevVariantRef.current = variant
  }, [variant, canUseHeadlines, canUseAspectRatio])

  useEffect(() => {
    if (!canUseHeadlines) {
      setHeadlineOverlay(false)
    }
  }, [canUseHeadlines])

  useEffect(() => {
    if (!canUseAspectRatio) {
      setAspectRatio(false)
      return
    }

    if (!manuallyDisabledAspectRatioRef.current) {
      setAspectRatio(true)
    }
  }, [selectedSubGroupId, canUseAspectRatio])

  useEffect(() => {
    if (!isGenerationLimitReached || variant !== 'open') {
      return
    }

    setVariant('closed')
    setSelectedSubGroupId?.(null)
  }, [isGenerationLimitReached, variant, setVariant, setSelectedSubGroupId])

  const isGenerateButtonEnabled = (aspectRatio || headlineOverlay)
    && Boolean(selectedSubGroupId)
    && generatedImageCount > 0
    && !isCreditCheckBlocking

  const handleGenerate = () => {
    onGenerate?.({
      media,
      selectedSubGroupId,
      ratioVariants: aspectRatio ? 'true' : null,
      headlines: headlineOverlay ? filteredHeadlines : [],
      requiredSlotRatios,
    })
  }

  const headlineTooltipBody = !hasHeadlines
    ? t('Tips.EventEdit.coCreateHeadlineTooltip.noHeadlines')
    : t('Tips.EventEdit.coCreateHeadlineTooltip.limitReached', {
      countToRemove,
      countToCreate,
    })

  const showHeadlineTooltip = !canUseHeadlines
  const showAspectRatioTooltip = allAspectRatioSlotsFilled
  const panelTooltipBody = isCampaignRunning
    ? t('Tips.EventEdit.coCreatePanelTooltip.body')
    : t('Tips.EventEdit.coCreatePanelLimitReached.body')

  // return
  return (
    <>
      {showGenerationError && (
        <ErrorMessage variant="light" style={{ justifyContent: 'flex-start' }}>
          {t('Recommendations.campaign.setup.coCreatePanel.generatingError')}
        </ErrorMessage>
      )}

      <InfoBox
        enabled={isPanelDisabled}
        body={panelTooltipBody}
        withAnchor={false}
      >
        <div
          className={classnames('co-create-panel', {
            'is-clickable': variant === 'closed' && !isPanelDisabled,
            'is-disabled': isPanelDisabled,
          })}
        >
          <CoCreatePanelHeader
            variant={variant}
            setVariant={setVariant}
            onViewVariants={onViewVariants}
            setSelectedSubGroupId={setSelectedSubGroupId}
            isPanelDisabled={isPanelDisabled}
          />

          {variant === 'open' && (
            <div className="co-create-panel-content">
              <InfoBox
                enabled={showAspectRatioTooltip}
                body={t('Tips.EventEdit.coCreateAspectRatioTooltip.body')}
                withAnchor={false}
              >
                <div className="switch-wrapper">
                  <Switch
                    checked={aspectRatio}
                    disabled={!canUseAspectRatio}
                    onClick={() => {
                      if (!canUseAspectRatio) return

                      setAspectRatio((prev) => {
                        const next = !prev
                        manuallyDisabledAspectRatioRef.current = !next
                        return next
                      })
                    }}
                  />

                  <div>
                    <p className="title">
                      {t('Recommendations.campaign.setup.coCreatePanel.generateAspectRatio.title')}
                    </p>
                    <p className="description">
                      {t(
                        'Recommendations.campaign.setup.coCreatePanel.generateAspectRatio.description',
                      )}
                    </p>
                  </div>
                </div>
              </InfoBox>

              <InfoBox enabled={showHeadlineTooltip} body={headlineTooltipBody} withAnchor={false}>
                <div className="switch-wrapper" onClick={() => {
                  if (!hasHeadlines) {
                    onRequestHeadlines?.()
                    return
                  }
                }}>
                  <Switch
                    checked={headlineOverlay}
                    disabled={!canUseHeadlines}
                    onClick={() => canUseHeadlines && setHeadlineOverlay((prev) => !prev)}
                  />

                  <div>
                    <p className="title">
                      {t('Recommendations.campaign.setup.coCreatePanel.headlineOverlay.title')}
                    </p>
                    <p className="description">
                      {t(
                        'Recommendations.campaign.setup.coCreatePanel.headlineOverlay.description',
                      )}
                    </p>
                  </div>
                </div>
              </InfoBox>
            </div>
          )}

          <CoCreateCredits
            creditStatus={creditStatus}
            error={creditStatusError}
            onRetry={onRetryCreditStatus}
            showSummary={variant === 'open'}
            totalCost={totalCreditCost}
            hasInsufficientCredits={hasInsufficientCredits}
          />
          {variant === 'open' && (
            <div className="co-create-panel-footer"> 
                <div className={`remaining-credits-wrapper ${hasInsufficientCredits || !isNumber(remainingCredits) ? 'warning' : ''}`}>
                  {isNumber(remainingCredits) || creditStatusLoading ? <WalletMinimalIcon width={14} height={14} /> : <TriangleAlertIcon width={14} height={14} /> }

                  {isNumber(remainingCredits) || creditStatusLoading ? 
                  <span className="remaining-credits-value">{creditStatusLoading ? '...' : remainingCredits + ' ' + t('common.credits_plural')}</span> 
                    : 
                  <span className="remaining-credits-value">{t('common.creditsUnavailable')}</span>}
                </div>

                <div className="generate-button-wrapper">
                  {selectedGroup ?
                   <div className="variants-and-credits-wrapper">
                    <span className="variants-value">{generatedImageCount > 1 ? t('common.variants', { count: generatedImageCount }) : t('common.variant')}</span>

                    <span className="separator">•</span>

                    <span className="credits-value">{t('common.creditsNeeded', { count: Math.round(totalCreditCost) })}</span>
                   </div> 
                   : 
                   <span className="variants-and-credits-wrapper">{t('Recommendations.campaign.setup.coCreatePanel.credits.selectImageToSeeCost')}</span>
                  }

                  <GenerateButton
                    disabled={!isGenerateButtonEnabled}
                    onClick={handleGenerate}
                  />
                </div>
            </div>
          )}
        </div>
      </InfoBox>
    </>
  )
}

CoCreateImagePanel.propTypes = {
  variant: PropTypes.oneOf(['closed', 'open', 'generating', 'ready']).isRequired,
  setVariant: PropTypes.func.isRequired,
  selectedSubGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedSubGroupId: PropTypes.func,
  media: PropTypes.arrayOf(PropTypes.object),
  headlines: PropTypes.arrayOf(PropTypes.string),
  maxItems: PropTypes.number,
  generatedMediaCount: PropTypes.number,
  showGenerationError: PropTypes.bool,
  onGenerate: PropTypes.func,
  isCampaignRunning: PropTypes.bool,
  onRequestHeadlines: PropTypes.func,
  requiredSlotRatios: PropTypes.arrayOf(PropTypes.string),
  onViewVariants: PropTypes.func,
  creditStatus: PropTypes.object,
  creditStatusError: PropTypes.string,
  creditStatusLoading: PropTypes.bool,
  onRetryCreditStatus: PropTypes.func,
}
